import {
  createContext,
  useContext,
  useState,
  type ReactNode,
  useEffect,
} from "react";
import { useLocation } from "react-router-dom";
import { useUserContext } from "./UserContext";
import { getAllRetailers, type RetailerType } from "../config/retailers";

export type ProductType = RetailerType;

interface ProductContextType {
  selectedProduct: ProductType;
  setSelectedProduct: (product: ProductType) => void;
}

const ProductContext = createContext<ProductContextType | undefined>(undefined);

interface ProductProviderProps {
  children: ReactNode;
}

const VALID_RETAILERS: ProductType[] = getAllRetailers();

function getRetailerFromUrl(): ProductType | null {
  const seg = window.location.pathname.split("/")[1];
  return seg && VALID_RETAILERS.includes(seg as ProductType) ? (seg as ProductType) : null;
}

function getDefaultProduct(retailers: string[]): ProductType {
  const retailer = retailers[0];
  if (!retailer) return "walmart";
  const split = retailer.split("_");
  const candidate = (split.length > 1
    ? split.slice(0, split.length - 1).join("_")
    : retailer) as ProductType;
  return VALID_RETAILERS.includes(candidate) ? candidate : "walmart";
}

export function ProductProvider({ children }: ProductProviderProps) {
  const { user } = useUserContext();
  const [selectedProduct, setSelectedProduct] = useState<ProductType>(
    () => getRetailerFromUrl() || getDefaultProduct(user?.retailers || [])
  );

  return (
    <ProductContext.Provider value={{ selectedProduct, setSelectedProduct }}>
      {children}
    </ProductContext.Provider>
  );
}

export function useProductContext() {
  const context = useContext(ProductContext);
  if (context === undefined) {
    throw new Error("useProductContext must be used within a ProductProvider");
  }
  return context;
}

// Hook to sync selected product with URL - must be used inside Router context
export function useProductUrlSync() {
  const { setSelectedProduct } = useProductContext();
  const location = useLocation();

  useEffect(() => {
    const seg = location.pathname.split("/")[1];
    if (seg && VALID_RETAILERS.includes(seg as ProductType)) {
      setSelectedProduct(seg as ProductType);
    }
  }, [location.pathname, setSelectedProduct]);
}
